import { Box, Checkbox, CircularProgress, FormControlLabel, Grid, Link, TextField, Typography, useTheme } from '@mui/material'
import { ChangeEvent, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
// Components
import { Formtitle, IperiaLayout } from '../../components'
// Hooks
import { useIperiaStatus } from '../../hooks/useIperiaStatus'
// Utils
import { extractNirDigits, formatNirInput, getNirError, isValidNir } from '../../utils/nir'

const Nir = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { global_id: globalId } = useParams<{ global_id: string }>()
  const { status, loading, error, submitNir } = useIperiaStatus()
  const [nir, setNir] = useState('')
  const [authorizationDataUse, setAuthorizationDataUse] = useState(false)
  // Born in Corsica: the NIR carries a 2A/2B letter, so the mobile keyboard must offer letters.
  const [bornInCorsica, setBornInCorsica] = useState(false)
  const [touched, setTouched] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    if (!status?.learner_summary) return
    const savedNir = formatNirInput(status.learner_summary.social_security_number || '')
    setNir(savedNir)
    setBornInCorsica(/[AB]/.test(savedNir))
    setAuthorizationDataUse(Boolean(status.learner_summary.authorization_data_use))
  }, [status?.learner_summary])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error || !status) {
    return (
      <Typography color="error" sx={{ p: 2 }}>
        Impossible de charger votre dossier.
      </Typography>
    )
  }

  const nirError = getNirError(nir)
  const showError = touched && nirError !== null
  const alreadyPastNir = Boolean(status.can_proceed_to_documents)
    || ['awaiting_am_documents', 'documents_under_review', 'documents_rejected', 'awaiting_parent_employeur', 'awaiting_bi_signature', 'bi_blocked_late_start', 'awaiting_pe_signature', 'pe_signed', 'completed'].includes(status.workflow_status)
  const canSubmit = alreadyPastNir || (isValidNir(nir) && authorizationDataUse)

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setNir(formatNirInput(event.target.value))
  }

  const handleSubmit = async () => {
    if (!canSubmit) return

    if (alreadyPastNir) {
      navigate(`/iperia/${globalId}/documents`)
      return
    }

    setSubmitting(true)
    setSubmitError(null)
    try {
      await submitNir(extractNirDigits(nir), authorizationDataUse)
      navigate(`/iperia/${globalId}/documents`)
    } catch {
      setSubmitError('Une erreur est survenue. Veuillez réessayer.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleBack = () => {
    navigate(`/iperia/${globalId}/verification/recapitulatif`)
  }

  return (
    <IperiaLayout
      productName={status.learner_summary.product_name}
      handleBack={handleBack}
      handleNext={handleSubmit}
      submitting={submitting}
      submittingNext={submitting || !canSubmit}
      alreadyConfirmed={alreadyPastNir}
    >
      <Formtitle
        title="Numéro de sécurité sociale"
        subtitle="Saisissez votre numéro de sécurité sociale et acceptez l'autorisation requise."
      />
      <Grid container direction="column" spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Numéro de sécurité sociale"
            placeholder="2 55 08 14 167 054 39"
            value={nir}
            onChange={handleChange}
            onBlur={() => setTouched(true)}
            error={showError}
            helperText={showError ? nirError : 'Format attendu : 15 caractères, tel qu\'indiqué sur votre carte Vitale.'}
            inputProps={{
              inputMode: bornInCorsica ? 'text' : 'numeric',
              autoComplete: 'off',
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={bornInCorsica}
                onChange={(e) => setBornInCorsica(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2" style={{ color: theme.palette.primary.dark }}>
                Je suis né(e) en Corse (mon numéro contient 2A ou 2B)
              </Typography>
            }
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body1" gutterBottom style={{ color: theme.palette.primary.dark }}>
          Vous consentez à ce qu'Edumiam conserve les pièces transmises dans un logiciel sécurisé. Cette étape est indispensable pour suivre la formation.
          </Typography>
          <Typography variant="body2" gutterBottom style={{ color: theme.palette.primary.dark }}>
          Une fois la formation terminée, vous pouvez retirer votre consentement en écrivant à <Link href="mailto:assmat@edumiam.com" sx={{ color: theme.palette.primary.dark, textDecoration: 'underline' }}>assmat@edumiam.com</Link>.
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={authorizationDataUse}
                onChange={(e) => setAuthorizationDataUse(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2" style={{ color: theme.palette.primary.dark }}>
                J'autorise l'utilisation de mes données personnelles dans le cadre de cette formation IPERIA
              </Typography>
            }
          />

        </Grid>

        {submitError && (
          <Grid item xs={12}>
            <Typography color="error">{submitError}</Typography>
          </Grid>
        )}
      </Grid>
    </IperiaLayout>
  )
}

export default Nir
